/* eslint-disable no-underscore-dangle -- the __dhlConfig variable is named this way */
import { type AvailableFeatures, isFeatureKeyEnabled, buildConfig, topLevelDomainUtils } from '@dhl/receiver-config'
import {
  type JsonConfiguration,
  accountTlds,
  type AccountTlds,
  type AccountAppConfiguration,
} from '../configuration/appconfiguration'
import localJsonConfig from '../configuration/local.json'

type AccountInjectedConfig = { CSP_NONCE: string; ENVIRONMENT: string; VERSION: string }

declare global {
  interface Window {
    __dhlConfig: JsonConfiguration<AccountTlds> & AccountInjectedConfig
  }
}

export const { getTopLevelDomain, forTopLevelDomain, buildConfigForTld } =
  topLevelDomainUtils.buildDomainUtils<AccountTlds>({
    supportedTlds: accountTlds,
    defaultTld: 'nl',
    tldOverride: process.env.REACT_APP_UTIL_TOP_LEVEL_DOMAIN_OVERRIDE || window.__dhlConfig?.default.util.tldOverride,
    frontendDomain: 'my.dhlparcel',
  })

/**
 * ppl (.cz) domain determination, adjust accordingly for other domains if needed
 */
const pplTopLevelDomains: readonly AccountTlds[number][] = ['cz']

export const isPplTopLevelDomain = (tld: AccountTlds[number] = getTopLevelDomain()): boolean =>
  pplTopLevelDomains.includes(tld)

export const isPplDomain = (hostname?: string): boolean => isPplTopLevelDomain(getTopLevelDomain(hostname))

/**
 * Build configuration
 */
const buildConfigInstance = () => {
  const tld = getTopLevelDomain()
  const isDevelopment = process.env.NODE_ENV !== 'production'

  // In development mode we use the local config, otherwise get the runtime config (i.e. window.__dhlConfig)
  const baseConfig = buildConfigForTld<AccountAppConfiguration, JsonConfiguration<AccountTlds>>({
    config: isDevelopment ? localJsonConfig : window.__dhlConfig,
    tld,
  })

  // We need to add some config settings that are not yet definable in the JSON config
  const additionalConfig = () => ({
    cspNonce: window.__dhlConfig?.CSP_NONCE ?? '',
    environment: window.__dhlConfig?.ENVIRONMENT ?? 'development',
    isDevelopment,
    version: window.__dhlConfig?.VERSION ?? 'latest',
  })

  return buildConfig<AccountAppConfiguration, ReturnType<typeof additionalConfig>>({
    baseConfig,
    additionalConfig,
  })
}

/**
 * Configuration singleton
 */
let configInstance: ReturnType<typeof buildConfigInstance> | null = null

export const getConfig = (): ReturnType<typeof buildConfigInstance> => {
  configInstance ??= buildConfigInstance()
  return configInstance
}

/**
 * Feature enabled utils
 */
type FeatureKeys = AvailableFeatures<AccountAppConfiguration['features']>
export const isFeatureEnabled = (...featureKeys: FeatureKeys[]): boolean => {
  const config = getConfig()
  return featureKeys.some((featureKey) => isFeatureKeyEnabled(featureKey, config))
}
